// Register ajaxCart component before Alpine starts
// Prefix cart endpoints with the active market root (e.g. "/en-us/") so /cart.js
// returns the localized currency and item URLs. Without this, fetches hit the
// global storefront and the drawer shows GBP / 404s on US-only product handles.
const cartUrl = (path) => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.replace(/\/$/, '') + '/' + path.replace(/^\//, '');
};

// Cart attribute set when the customer removes an auto-added gift. Read by the
// gwp-checkout-ui extension (monpure-tool-kit) too, so keep the key in sync.
// No leading underscore: it shows on the order in admin as a record of the choice.
const GWP_DECLINED_ATTRIBUTE = 'gwp_declined';

document.addEventListener('alpine:init', () => {
  Alpine.data('ajaxCart', () => ({
    cart: {
      items: [],
      total_price: 0,
      item_count: 0,
      original_total_price: 0
    },
    isOpen: false,
    isLoading: false,
    currency: 'GBP',
    errorMessage: null,

    // Compare-at-price cache (variant_id -> price in cents)
    compareAtPrices: {},

    // Sample variant IDs (hidden quantity selector)
    sampleVariantIds: new Set(),

    // GWP (Gift With Purchase) state
    gwpConfig: null,
    gwpProcessing: false,
    gwpPendingVariants: new Set(),

    init() {
      // Load GWP config from window
      this.gwpConfig = window.MONPURE_GWP_CONFIG || { enabled: false, tiers: [] };

      // Load initial compare_at_price data from Liquid-rendered JSON
      this.loadCompareAtPrices();
      this.loadSampleVariantIds();

      this.fetchCart();

      // Event Listeners
      window.addEventListener('ajax-cart:add', (event) => {
        this.addToCart(event.detail.formData);
      });

      window.addEventListener('ajax-cart:toggle', () => {
        this.toggle();
      });
      
      window.addEventListener('ajax-cart:open', () => {
        this.open();
      });

      window.addEventListener('cart:updated', () => {
        this.fetchCart();
      });
      
      window.addEventListener('cart:refresh', () => {
        this.fetchCart();
      });

      this.watchExternalCartWrites();

      // Hijack legacy cart links
      document.addEventListener('click', (e) => {
        const cartLink = e.target.closest('a[href="/cart"]');
        if (cartLink) {
          e.preventDefault();
          this.open();
        }
      });
      
      // Close on escape key
      this.$watch('isOpen', (value) => {
        if (value) {
            this.trapFocus();
            document.body.classList.add('cart-drawer-active');
        } else {
            this.releaseFocus();
            document.body.classList.remove('cart-drawer-active');
        }
      });
      
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Listen for store-triggered opens
      window.addEventListener('cart-drawer:open', () => this.open());
    },

    updateHeaderCount(count) {
        document.querySelectorAll('.cart-desctop-count-js, .cart-mob-count-js').forEach(el => {
            el.setAttribute('data-count', count);
            const badge = el.querySelector('.cart-count');
            if (badge) badge.textContent = count;
        });
    },

    open() {
      this.isOpen = true;
      // Dispatch for store/other scripts
      window.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    },

    close() {
      this.isOpen = false;
      window.dispatchEvent(new CustomEvent('cart-drawer:closed'));
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    trapFocus() {
        setTimeout(() => {
            const closeBtn = this.$root.querySelector('.cd-close-btn');
            if (closeBtn) closeBtn.focus();
        }, 300); // Wait for transition
    },
    
    releaseFocus() {},

    loadCompareAtPrices() {
      try {
        const el = document.getElementById('compare-at-prices');
        if (el) {
          const data = JSON.parse(el.textContent);
          this.compareAtPrices = { ...this.compareAtPrices, ...data };
        }
      } catch (e) { /* ignore parse errors */ }
    },

    loadSampleVariantIds() {
      try {
        const el = document.getElementById('sample-variant-ids');
        if (el) {
          const ids = JSON.parse(el.textContent);
          this.sampleVariantIds = new Set(ids);
        }
      } catch (e) { /* ignore parse errors */ }
    },

    isSample(item) {
      return this.sampleVariantIds.has(item.variant_id);
    },

    async refreshCompareAtPrices() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=ajax-cart-drawer`);
        const sections = await res.json();
        if (sections['ajax-cart-drawer']) {
          const temp = document.createElement('div');
          temp.innerHTML = sections['ajax-cart-drawer'];
          const priceEl = temp.querySelector('#compare-at-prices');
          if (priceEl) {
            const data = JSON.parse(priceEl.textContent);
            this.compareAtPrices = data;
          }
          const sampleEl = temp.querySelector('#sample-variant-ids');
          if (sampleEl) {
            const ids = JSON.parse(sampleEl.textContent);
            this.sampleVariantIds = new Set(ids);
          }
        }
      } catch (e) { /* ignore */ }
    },

    async fetchCart() {
      try {
        const response = await fetch(cartUrl('cart.js'));
        const cart = await response.json();
        this.updateFromCart(cart);
        this.refreshCompareAtPrices();
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    },

    updateFromCart(cart) {
        this.cart = cart;
        this.currency = cart.currency;
        this.updateHeaderCount(cart.item_count);

        if (window.Alpine && Alpine.store('cart')) {
            const store = Alpine.store('cart');
            // Update store data but avoid infinite loops
            store.items = cart.items;
            store.itemCount = cart.item_count;
            store.totalPrice = cart.total_price;
            store.totalDiscount = cart.total_discount || 0;
            store.originalTotalPrice = cart.original_total_price || cart.total_price;
        }

        // Manage GWP gifts after cart update
        this.manageGwpGifts();
    },

    async addToCart(formData) {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        const pairingEl = document.querySelector('[data-product-pairing][data-pairing-active="true"]');
        let response;

        if (pairingEl) {
          const pairingVariantId = pairingEl.getAttribute('data-pairing-variant-id');
          const mainVariantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                { id: parseInt(mainVariantId, 10), quantity: quantity },
                { id: parseInt(pairingVariantId, 10), quantity: 1 }
              ]
            })
          });
        } else {
          response = await fetch(cartUrl('cart/add.js'), {
            method: 'POST',
            body: formData
          });
        }

        if (response.ok) {
          await this.fetchCart();
          this.open();
        } else {
          const data = await response.json();
          this.showError(data.description ? data.description : 'Error adding to cart');
        }
      } catch (error) {
        this.showError('Network error. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },

    async changeItem(key, quantity) {
      this.isLoading = true;
      // Removing an auto-added gift opts the cart out of the GWP. The flag is a
      // cart attribute, written in the same request as the removal, so the
      // checkout extension sees it too and does not put the gift back.
      const removed = quantity === 0 && (this.cart.items || []).find(item => item.key === key);
      const declinesGift = Boolean(removed && this.isAutoGwpItem(removed));
      try {
        const response = declinesGift
          ? await fetch(cartUrl('cart/update.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                updates: { [key]: 0 },
                attributes: { [GWP_DECLINED_ATTRIBUTE]: 'true' }
              })
            })
          : await fetch(cartUrl('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: key, quantity: quantity })
            });

        const cart = await response.json();
        this.updateFromCart(cart);
      } catch (error) {
        this.fetchCart();
      } finally {
        this.isLoading = false;
      }
    },

    async removeItem(key) {
      await this.changeItem(key, 0);
    },

    compareAtTotal() {
      let total = 0;
      for (const item of this.cart.items || []) {
        const cap = this.compareAtPrices[item.variant_id];
        const spaCap = item.selling_plan_allocation && item.selling_plan_allocation.compare_at_price;
        if (cap && cap > item.final_price) {
          total += cap * item.quantity;
        } else if (spaCap && spaCap > item.final_price) {
          // Subscription line: use the selling plan's RRP so the subtotal markdown reflects the saving
          total += spaCap * item.quantity;
        } else {
          total += item.final_line_price;
        }
      }
      return total;
    },

    money(cents) {
        if (typeof cents !== 'number') return '';
        return (cents / 100).toLocaleString(undefined, {
            style: 'currency',
            currency: this.currency
        }).replace('GBP', '£').replace('USD', '$').replace('EUR', '€');
    },
    
    showError(msg) {
        this.errorMessage = msg;
        setTimeout(() => { this.errorMessage = null; }, 4000);
    },

    // ========== GWP (Gift With Purchase) Methods ==========
    // SKU-based condition rules: a rule qualifies when ANY condition group matches.
    // Within a group, ALL variants must be present with min_quantity in cart.

    /**
     * Watch for cart writes made outside this component (apps such as Appstle
     * or Hulkapps, and any theme code that posts to /cart/* directly without
     * dispatching cart:updated). Those adds would otherwise never trigger the
     * gift check, leaving a qualifying cart with no gift line in it until the
     * next page load. Patches fetch and XHR once, and only re-checks after a
     * write we did not make ourselves — manageGwpGifts guards its own calls
     * with gwpProcessing, so the gift add/remove traffic cannot loop back here.
     */
    watchExternalCartWrites() {
      if (window.__monpureCartWatchInstalled) return;
      window.__monpureCartWatchInstalled = true;

      const isCartWrite = (url) =>
        typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);

      let pending = null;
      const scheduleRecheck = () => {
        if (this.gwpProcessing) return;
        clearTimeout(pending);
        pending = setTimeout(() => this.fetchCart(), 300);
      };

      const originalFetch = window.fetch;
      window.fetch = (...args) => {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url);
        return originalFetch.apply(window, args).then((response) => {
          if (isCartWrite(url) && response.ok) scheduleRecheck();
          return response;
        });
      };

      const originalOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__monpureCartWrite = isCartWrite(url);
        return originalOpen.call(this, method, url, ...rest);
      };
      const originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (...args) {
        if (this.__monpureCartWrite) {
          this.addEventListener('load', () => {
            if (this.status >= 200 && this.status < 300) scheduleRecheck();
          });
        }
        return originalSend.apply(this, args);
      };
    },

    /**
     * Is a gift line for an eligible rule actually in the cart right now?
     * Drives the drawer card's "added to your bag" vs "claim it" states.
     */
    hasGwpGiftInCart() {
      const eligible = new Set(this.getEligibleRules().map(r => r.gift_variant_id));
      if (eligible.size === 0) return false;
      return (this.cart.items || []).some(
        item => eligible.has(`gid://shopify/ProductVariant/${item.variant_id}`)
      );
    },

    /**
     * Manual claim used by the drawer card. Covers the customer who removed the
     * gift earlier and changed their mind, and acts as a visible fallback if an
     * automatic add ever fails.
     */
    async addGwpGift() {
      const eligible = this.getEligibleRules();
      if (!eligible.length || this.gwpProcessing) return;

      // Opt back in: clear the declined flag (an empty value deletes a cart
      // attribute) before adding, or manageGwpGifts would skip the add.
      if (this.gwpDeclined()) {
        try {
          const response = await fetch(cartUrl('cart/update.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { [GWP_DECLINED_ATTRIBUTE]: '' } })
          });
          this.cart = await response.json();
        } catch (error) {
          console.error('GWP: Error clearing the declined flag', error);
          return;
        }
      }

      // The in-flight guard still holds this variant from the earlier automatic
      // add (it is only cleared once the gift is seen in the cart, which never
      // happens after a removal). Release it, or manageGwpGifts skips the add.
      for (const rule of eligible) {
        this.gwpPendingVariants.delete(rule.gift_variant_id);
      }

      await this.manageGwpGifts();
    },

    /**
     * Get all gift variant IDs from config (GID format)
     */
    getGwpVariantIds() {
      if (!this.gwpConfig?.rules) return new Set();
      return new Set(this.gwpConfig.rules.map(rule => rule.gift_variant_id));
    },

    /**
     * True once the customer has removed an auto-added gift from this cart.
     * Nothing is auto-added after that (here or in the checkout extension)
     * until they claim a gift from the drawer again.
     */
    gwpDeclined() {
      return this.cart?.attributes?.[GWP_DECLINED_ATTRIBUTE] === 'true';
    },

    /**
     * Check if a cart item is an auto-added GWP gift
     */
    isAutoGwpItem(item) {
      const gwpVariantIds = this.getGwpVariantIds();
      if (!gwpVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`)) {
        return false;
      }
      return item.properties && item.properties._auto_gwp === 'true';
    },

    /**
     * Check if a cart item is any GWP gift (auto or manual)
     */
    isGwpItem(item) {
      const gwpVariantIds = this.getGwpVariantIds();
      return gwpVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`);
    },

    /**
     * Build a map of variant GID → total quantity in cart (excluding auto-added gifts).
     * Used for SKU-based condition matching.
     */
    buildVariantQtyMap() {
      const qtyMap = new Map();
      for (const item of this.cart.items || []) {
        if (this.isAutoGwpItem(item)) continue;
        const gid = `gid://shopify/ProductVariant/${item.variant_id}`;
        qtyMap.set(gid, (qtyMap.get(gid) || 0) + item.quantity);
      }
      return qtyMap;
    },

    /**
     * Non-gift cart subtotal in major currency units (e.g. £, €, $). Only the
     * auto-added gift lines are excluded: a gift-variant product the customer
     * added themselves is a normal purchase and counts toward the threshold
     * (the transform never zeroes a line without `_auto_gwp`).
     * Mirrors the Rust cart_transform's cart_subtotal and the checkout
     * extension's nonGiftSubtotal — must agree across all three layers,
     * otherwise the gift line will flap as one side adds and another removes.
     */
    nonGiftSubtotal() {
      let pence = 0;
      for (const item of this.cart.items || []) {
        if (this.isAutoGwpItem(item)) continue;
        pence += item.final_line_price != null
          ? item.final_line_price
          : (item.original_line_price || 0);
      }
      return pence / 100;
    },

    /**
     * Get eligible GWP rules. A rule qualifies when EITHER any condition
     * group matches (variant-based) OR the non-gift cart subtotal meets the
     * threshold for the active currency. OR semantics match the Rust
     * cart transform's rule_qualifies() and the checkout UI extension.
     * With config.tier_mode === 'highest' the qualifying spend-threshold
     * rules collapse to the highest threshold reached (see applyGwpTierMode).
     */
    getEligibleRules() {
      if (!this.gwpConfig?.enabled || !this.gwpConfig?.rules) {
        return [];
      }

      const variantQtyMap = this.buildVariantQtyMap();
      const subtotal = this.nonGiftSubtotal();
      const currency = this.currency || 'GBP';

      const eligible = this.gwpConfig.rules.filter(rule => {
        if (!rule.enabled) return false;

        const hasGroups = rule.condition_groups && rule.condition_groups.length > 0;
        const threshold = rule.thresholds && rule.thresholds[currency];
        const hasThreshold = typeof threshold === 'number';

        if (!hasGroups && !hasThreshold) return false;

        const groupsPass = hasGroups && rule.condition_groups.some(group =>
          group.variants.every(cv => {
            const qty = variantQtyMap.get(cv.variant_id) || 0;
            return qty >= cv.min_quantity;
          })
        );

        const thresholdPasses = hasThreshold && subtotal >= threshold;

        return groupsPass || thresholdPasses;
      });

      return this.applyGwpTierMode(eligible, currency);
    },

    /**
     * tier_mode 'highest': of the eligible rules carrying a threshold for this
     * currency keep only the one with the highest threshold (ties go to the
     * higher rule_level). Rules eligible purely through SKU conditions (no
     * threshold for this currency) are kept, so condition campaigns still
     * stack. Must match the Rust cart transform and gwp-checkout-ui.
     */
    applyGwpTierMode(eligible, currency) {
      if (this.gwpConfig?.tier_mode !== 'highest' || eligible.length <= 1) return eligible;

      let top = null;
      let topThreshold = -Infinity;
      for (const rule of eligible) {
        const threshold = rule.thresholds && rule.thresholds[currency];
        if (typeof threshold !== 'number') continue;
        if (threshold > topThreshold || (threshold === topThreshold && top && rule.rule_level > top.rule_level)) {
          top = rule;
          topThreshold = threshold;
        }
      }
      if (!top) return eligible;

      return eligible.filter(rule =>
        rule === top || typeof (rule.thresholds && rule.thresholds[currency]) !== 'number'
      );
    },

    /**
     * Spend-threshold tiers for the active currency, lowest threshold first.
     * Drives the tier ladder in the drawer (snippets/cart-gwp-tiers.liquid).
     */
    gwpTierRules() {
      if (!this.gwpConfig?.enabled || !this.gwpConfig?.rules) return [];
      const currency = this.currency || 'GBP';
      return this.gwpConfig.rules
        .filter(rule => rule.enabled && rule.thresholds && typeof rule.thresholds[currency] === 'number')
        .map(rule => ({ rule, threshold: rule.thresholds[currency] }))
        .sort((a, b) => (a.threshold - b.threshold) || (a.rule.rule_level - b.rule.rule_level));
    },

    /**
     * Where the cart sits on the tier ladder: the tiers, the non-gift subtotal,
     * the highest tier reached (`current`), the next tier to unlock (`next`)
     * and how much more spend it needs.
     */
    gwpTierState() {
      const tiers = this.gwpTierRules();
      const subtotal = this.nonGiftSubtotal();
      const reached = tiers.filter(t => subtotal >= t.threshold);
      const current = reached.length ? reached[reached.length - 1] : null;
      const next = tiers.find(t => subtotal < t.threshold) || null;
      return {
        tiers,
        subtotal,
        current,
        next,
        amountToNext: next ? Math.max(0, next.threshold - subtotal) : 0
      };
    },

    /**
     * Display state of one tier row in the drawer ladder:
     *  'current'  - highest tier reached and its gift is in the bag
     *  'claim'    - highest tier reached but the gift line is missing
     *               (customer removed it): offer the claim button
     *  'upgraded' - a lower tier the cart has passed (highest mode: superseded)
     *  'next'     - the next tier to unlock
     *  'locked'   - further away
     */
    gwpTierRowState(giftVariantId) {
      const state = this.gwpTierState();
      const tier = state.tiers.find(t => t.rule.gift_variant_id === giftVariantId);
      if (!tier) return 'hidden';
      const reached = state.subtotal >= tier.threshold;
      const highestOnly = this.gwpConfig?.tier_mode === 'highest';
      const isCurrent = reached && (!highestOnly || (state.current && state.current.rule.gift_variant_id === giftVariantId));
      if (isCurrent) {
        const inCart = (this.cart.items || []).some(
          item => `gid://shopify/ProductVariant/${item.variant_id}` === giftVariantId && this.isAutoGwpItem(item)
        );
        return inCart ? 'current' : 'claim';
      }
      if (reached) return 'upgraded';
      if (state.next && state.next.rule.gift_variant_id === giftVariantId) return 'next';
      return 'locked';
    },

    /**
     * Main method to manage GWP gifts - add eligible, remove ineligible.
     * Runs after every cart update to check SKU-based conditions.
     */
    async manageGwpGifts() {
      // Deliberately not gated on gwpConfig.enabled. When a campaign is switched
      // off in the toolkit the config stays in place with enabled:false, so gifts
      // already sitting in a shopper's cart can still be identified and cleared.
      // With the campaign off no rule is eligible, so the logic below resolves to
      // "remove everything auto-added, add nothing" — which is what a shopper who
      // built their cart during the campaign should see once it ends.
      if (this.gwpProcessing) {
        return;
      }

      const gwpVariantIds = this.getGwpVariantIds();
      if (gwpVariantIds.size === 0) return;

      const eligibleRules = this.getEligibleRules();
      const eligibleVariantIds = new Set(eligibleRules.map(r => r.gift_variant_id));

      // Find auto-added GWP items currently in cart
      const autoGwpItems = (this.cart.items || []).filter(item => this.isAutoGwpItem(item));
      const autoGwpVariantIds = new Set(
        autoGwpItems.map(item => `gid://shopify/ProductVariant/${item.variant_id}`)
      );

      // Clean up pending refs - remove variants now in cart
      for (const variantId of this.gwpPendingVariants) {
        if (autoGwpVariantIds.has(variantId)) {
          this.gwpPendingVariants.delete(variantId);
        }
      }

      // Clear pending for variants no longer eligible
      for (const variantId of this.gwpPendingVariants) {
        if (!eligibleVariantIds.has(variantId)) {
          this.gwpPendingVariants.delete(variantId);
        }
      }

      // Determine variants to ADD (eligible but not in cart, not pending), unless
      // the customer has declined the gift for this cart
      const variantsToAdd = this.gwpDeclined() ? [] : [...eligibleVariantIds].filter(
        variantId => !autoGwpVariantIds.has(variantId) &&
          !this.gwpPendingVariants.has(variantId)
      );

      // Determine items to REMOVE (auto-added but no longer eligible)
      const itemsToRemove = autoGwpItems.filter(
        item => !eligibleVariantIds.has(`gid://shopify/ProductVariant/${item.variant_id}`)
      );

      if (variantsToAdd.length === 0 && itemsToRemove.length === 0) {
        return;
      }

      // Mark variants as pending
      for (const variantId of variantsToAdd) {
        this.gwpPendingVariants.add(variantId);
      }

      this.gwpProcessing = true;

      try {
        await new Promise(resolve => setTimeout(resolve, 250));

        // Add eligible gift variants
        for (const variantId of variantsToAdd) {
          const numericId = variantId.split('/').pop();

          try {
            const response = await fetch(cartUrl('cart/add.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: numericId,
                quantity: 1,
                properties: {
                  _auto_gwp: 'true'
                }
              })
            });

            if (!response.ok) {
              console.error('GWP: Failed to add gift', numericId);
              this.gwpPendingVariants.delete(variantId);
            }
          } catch (error) {
            console.error('GWP: Error adding gift', error);
            this.gwpPendingVariants.delete(variantId);
          }
        }

        // Remove ineligible gift items
        for (const item of itemsToRemove) {
          try {
            await fetch(cartUrl('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
          } catch (error) {
            console.error('GWP: Error removing gift', error);
          }
        }

        // Refresh cart state without re-triggering manageGwpGifts (avoids loop)
        if (variantsToAdd.length > 0 || itemsToRemove.length > 0) {
          const response = await fetch(cartUrl('cart.js'));
          const cart = await response.json();

          this.cart = cart;
          this.currency = cart.currency;
          this.updateHeaderCount(cart.item_count);
          this.refreshCompareAtPrices();

          if (window.Alpine && Alpine.store('cart')) {
            const store = Alpine.store('cart');
            store.items = cart.items;
            store.itemCount = cart.item_count;
            store.totalPrice = cart.total_price;
            store.totalDiscount = cart.total_discount || 0;
            store.originalTotalPrice = cart.original_total_price || cart.total_price;
          }
        }
      } finally {
        this.gwpProcessing = false;
      }
    }
  }));
});